import React, {useEffect, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import filter from 'lodash/filter';
import find from 'lodash/find';

import {DashboardSidebarCollapsableList} from 'app/components/general/DashboardPage/DashboardSidebarCollapsableList';
import {convertLocationParamsFoldersDocumentsToFetchParams} from 'app/documents/api';
import {useFolderByHashid, useFolders} from 'app/folders/api';
import {gt} from 'app/utils/i18n';

import {FolderItem} from './FolderItem';

import {FolderHashidSerializedType, FolderSerializedType} from 'sweet-types';

import type {FolderItemProps} from './FolderItem';

const FolderItemSelectionWrapper = (props: FolderItemProps) => {
  const [checked, setCheckedFolders] = useState<boolean>(false);

  useEffect(() => {
    if (props.itemProps == null) {
      return;
    }

    if (
      find(
        props.itemProps.selectedItems,
        (item: FolderSerializedType) => item.url === props.folder.url
      ) != null
    ) {
      setCheckedFolders(true);
    } else {
      setCheckedFolders(false);
    }
  }, [props.itemProps && props.itemProps.selectedItems]);

  const onToggle = () => {
    if (props.itemProps != null) {
      props.itemProps.callback(!checked, props.folder);
    } else {
      setCheckedFolders(!checked);
    }
  };

  const initiallyExpanded =
    props.itemProps != null &&
    (props.itemProps.initialExpandedFolders || []).indexOf(props.folder.url) >
      -1;

  return (
    <div>
      <div
        style={{
          float: 'left',
        }}
        className="m-2"
      >
        <input
          type="checkbox"
          disabled={
            props.itemProps != null ? !props.itemProps.checkboxEnabled : true
          }
          checked={checked}
          onChange={onToggle}
        />
      </div>
      <FolderItem
        {...props}
        initiallyExpanded={initiallyExpanded}
        isHidingDocuments={true}
        isHidingCreateDocument={true}
        isHidingCreateFolder={
          props.itemProps != null ? !props.itemProps.allowFolderCreation : false
        }
        isHidingTeams={true}
        actionsGetter={() => []}
        FolderItemReplacement={FolderItemSelectionWrapper}
      />
    </div>
  );
};

type FolderItemFirstLevelWrapProps = {
  item: FolderSerializedType;
  callback: (arg0: any) => void;
  selectedItems: Array<FolderSerializedType>;
  checkboxEnabled: boolean;
  initialExpandedFolders?: Array<string>;
  allowFolderCreation: boolean;
};

const FolderItemFirstLevelWrap = ({
  item,
  callback,
  selectedItems,
  checkboxEnabled,
  initialExpandedFolders = [],
  allowFolderCreation,
}: FolderItemFirstLevelWrapProps) => (
  <FolderItemSelectionWrapper
    folder={item}
    afterAction={() => Promise.resolve(true)}
    isParentDirty={false}
    itemProps={{
      selectedItems,
      callback,
      checkboxEnabled,
      allowFolderCreation,
      initialExpandedFolders,
    }}
  />
);

type Props = {
  onChange: (arg0: Array<FolderSerializedType>) => Promise<any>;
  initialSelectedFolder?: null | string;
  //items?: Array<FolderType>,
  checkboxEnabled?: boolean;
  allowFolderCreation?: boolean;
};
export const CollapsableFolderPickerForm = ({
  onChange,
  initialSelectedFolder,
  checkboxEnabled = true,
  allowFolderCreation = true,
}: Props) => {
  const [selectedFolders, setSelectedFolders] = useState<
    Array<FolderSerializedType>
  >([]);
  const [initialExpanded, setInitialExpanded] = useState<Array<string>>([]);

  const {currentData: initialFolder, isLoading} = useFolderByHashid(
    initialSelectedFolder ? initialSelectedFolder : skipToken
  );

  useEffect(() => {
    if (initialFolder != null) {
      const [folder, ancestors] = extractAncestorsList(initialFolder);
      setSelectedFolders([folder]);
      onChange([folder]);
      setInitialExpanded(ancestors);
    }
  }, [initialFolder == null]);

  // delay the rendering until the initial folder is fetched
  if (initialSelectedFolder && isLoading) {
    return null;
  }

  const itemClickedCallback = (
    expanded: boolean,
    folder: FolderSerializedType
  ) => {
    const folderExists = find(
      selectedFolders,
      (f) => f.hashid === folder.hashid
    );

    if (folderExists == null) {
      setSelectedFolders([...selectedFolders, folder]);
      onChange([...selectedFolders, folder]);
    } else {
      setSelectedFolders(
        filter(selectedFolders, (f) => f.hashid !== folder.hashid)
      );
      onChange(filter(selectedFolders, (f) => f.hashid !== folder.hashid));
    }
  };

  return (
    <DashboardSidebarCollapsableList
      icon="fa-folder"
      title={gt('Folders')}
      reduxKey="folders"
      watchModelChange="folder"
      showSearch={false}
      noResultsMessage={gt('No folders.')}
      canCreate={false}
      createLink={''}
      searchPlaceholder={gt('Search folders...')}
      useDataHook={useFolders}
      paramsConverter={convertLocationParamsFoldersDocumentsToFetchParams}
      pageParam={'folders_page'}
      fetchParams={{
        permission: 'edit_procedure',
      }}
      itemNameProcessor={(item) => item.name}
      ItemComponent={FolderItemFirstLevelWrap}
      itemProps={{
        callback: itemClickedCallback,
        selectedItems: selectedFolders,
        initialExpandedFolders: initialExpanded,
        checkboxEnabled,
        allowFolderCreation,
      }}
    />
  );
};

function extractAncestorsList(
  folder: FolderHashidSerializedType
): [FolderSerializedType, Array<string>] {
  let parent = folder.parent;
  let parents: Array<string> = [];

  while (parent !== null) {
    parents.push(parent.url);
    parent = parent.parent;
  }

  const flatFolder = {...folder, parent: parents[0] || null};
  return [flatFolder, parents];
}
